// @flow

import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import { stripUnit } from 'polished';

import type { SpecResult } from '../utils/vortex/types';
import type { FlashType } from '../components/Flash';

import settings from '../config/settings';

import { commonSizes } from '../utils/vortex/calculate';
import renderSafeHTML from '../utils/render-safe-html';

import { ActionButton } from './Button';
import Icon from './Icon';
import { Select } from './Input';
import SpecResultSection from './SpecResultSection';
import SpecResultItem from './SpecResultItem';

const DISPLAY_HEIGHT = 300;

// const Blocker = styled.div`
//   background: rgba(255, 255, 255, 0.35);
//   height: calc(100% - ${props => stripUnit(props.theme.submitButtonHeight) + stripUnit(props.theme.headerHeight)}px);
//   position: fixed;
//   top: ${props => props.theme.headerHeight};
//   width: 100%;
//   z-index: 2;
// `;

const Display = styled.div`
  background-color: ${({ theme }) => theme.lightBg1};
  background-image: linear-gradient(to bottom, ${props => props.theme.lightBg1}, ${props => props.theme.lightBg2});
  box-shadow: 0 -1px 8px rgba(0, 0, 0, 0.25);
  padding-top: 0.5em;

  width: 100%;
  height: ${DISPLAY_HEIGHT}px;

  position: fixed;
  overflow: hidden;
  z-index: 3;

  bottom: ${props => props.theme.submitButtonHeight};
  top: calc(100% - ${props => DISPLAY_HEIGHT + stripUnit(props.theme.submitButtonHeight)}px);

  ${settings.navTransition && css`transition: 150ms ease all;`}

  > .ss-icon {
    border-radius: ${props => props.theme.borderRadius};
    color: black;
    cursor: pointer;
    display: block;
    height: 44px;
    line-height: 44px;
    overflow: hidden;
    padding-top: 3px;
    position: absolute;
    right: 0.5em;
    text-align: center;
    top: 0.5em;
    transition: 150ms linear background-color;
    width: 44px;
    &:hover {
      background-color: #DDD;
    }
  }

  ${props => props.isRevealed && css`
    height: calc(100% - ${props => stripUnit(props.theme.submitButtonHeight)}px);
    overflow-y: scroll;
    top: 0;
  `}
`;

const Gearbox = styled.div`
  background: white;
  border-radius: ${props => props.theme.borderRadius};
  color: ${props => props.theme.brand};
  display: inline-block;
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 1em;
  padding: 0.5em 0.75em;
`;

const Warnings = styled.div`
  margin: auto;
  max-width: ${props => props.theme.maxWidth};
  padding: 0 1.5em;
  section {
    background: white;
    border-radius: ${props => props.theme.borderRadius};
    box-shadow: 0 1px 1px rgba(0, 0, 0, 0.25);
    padding: 1em;
  }
  ul {
    color: ${props => props.theme.input.warning.color};
    list-style: initial;
    padding: 0 1em;
    text-align: left;
  }
`;

const Warning = styled.li`
  font-size: 12px;
  line-height: 1.6;
  margin-bottom: 0.5em;
  &:last-child {
    margin-bottom: 0;
  }
`;

const WarningTitle = styled.span`
  color: ${props => props.theme.input.warning.color};
`;

type Props = {|
  isRevealed: boolean,
  isValid: boolean,
  onBushingKit: (string) => void,
  onDismiss: () => void,
  onReveal: () => void,
  result: SpecResult,
  warnings: { [key: string]: FlashType }
|};

type State = {|
  bushingOptions: Array<string>,
  isRevealed: boolean,
  selectedBushing: string
|};

class SpecResultDisplay extends Component<Props, State> {

  static defaultProps = {
    isRevealed: false,
    isValid: true
  }

  constructor(props: Props) {
    super(props);

    const [bushingOptions, selectedBushing] = this.calcBushingOptions();

    this.state = {
      bushingOptions,
      isRevealed: this.props.isRevealed,
      selectedBushing
    };

    this.props.onBushingKit(selectedBushing);
  }

  calcBushingOptions = () => {
    const series = this.props.result.series;
    if (series == null) {
      return [];
    }

    const options = commonSizes(this.props.result.bushings).map(function([dec, frac]) {
      return `${frac} (${dec}")`;
    }).reverse();

    const selected = `VOR XBK${series} ${options[0]}`;

    return [options, selected];
  }

  componentDidUpdate = (prevProps: Props, prevState: State) => {
    if (prevProps.result.bushings !== this.props.result.bushings) {
      const [bushingOptions, selectedBushing] = this.calcBushingOptions();
      this.setState({
        bushingOptions,
        selectedBushing
      });
      this.props.onBushingKit(selectedBushing);
    }
  }

  componentWillUnmount = () => {
    this.toggleOverflow(true);
  }

  onBushingKitSelected = (event: SyntheticEvent<HTMLInputElement | HTMLSelectElement>) => {
    const selectedBushing = event.currentTarget.value;
    // console.log('onBushingKitSelected', selectedBushing);
    this.setState({ selectedBushing });
    this.props.onBushingKit(selectedBushing);
  }

  onCloseHandler = (event: SyntheticMouseEvent<>) => {
    this.revealDetails(false);
  }

  onDismissHandler = (event: SyntheticMouseEvent<>) => {
    this.props.onDismiss();
  }

  toggleOverflow(active: boolean = true) {
    if (!document.body) {
      return;
    }
    document.body.style.overflow = active ? '' : 'hidden';
  }

  renderBushingOptions = () => {
    const bushingOptions = this.state.bushingOptions;
    const series = this.props.result.series;

    if (series == null || !bushingOptions.length) {
      return;
    }

    // console.log('renderBushingOptions', bushingOptions, series);

    return (
      <div>
        <Select value={this.state.selectedBushing} onChange={this.onBushingKitSelected}>
          {bushingOptions.map((option) => {
            const kit = `VOR XBK${series} ${option}`;
            return <option key={option} value={kit}>{kit}</option>;
          })}
        </Select>
      </div>
    );
  }

  renderCalculations = () => {
    const result = this.props.result;
    if (this.state.isRevealed) {
      return (
        <div className="calculations">
          <SpecResultSection title="Conveyor Calculations">
            <SpecResultItem label="Weight on Conveyor" value={result.weightOnConveyor} abbr=" lbs" />
            <SpecResultItem label="Conveyor Angle" value={result.conveyorAngle} abbr="°" />
            <SpecResultItem label="Head Pulley Torque" value={result.headPulleyTorque} abbr=" ft-lbs" />
            <SpecResultItem label="Output RPM" value={result.outputRPM} abbr=" rpm" />
            <SpecResultItem label="HP Required" value={result.requiredHP} abbr=" hp" />
            <SpecResultItem label="SF" value={result.serviceFactor} />
            <SpecResultItem label={`Req. Output HP at ${parseFloat(result.actualSF).toFixed(2)}`} value={result.requiredOutputHP} abbr=" hp" />
          </SpecResultSection>
          <SpecResultSection title={result.smr ? `${result.smr} Specs` : 'Specs'}>
            <SpecResultItem label="Output HP Cap." value={result.outputHPCap} abbr=" hp" />
            <SpecResultItem label="Input HP Cap." value={result.inputHPCap} abbr=" hp" />
            <SpecResultItem label="Actual SMR Ratio" value={result.actualSMRRatio} abbr=":1" />
            <SpecResultItem label="Sheave Ratio" value={result.sheaveRatio} abbr=":1" />
            <SpecResultItem label="Max Shaft Size" value={result.maxShaftSize} abbr=" in" />
            <SpecResultItem label="Actual SF" value={result.actualSF} />
            <SpecResultItem label="HPt" value={result.hpt} abbr=" hp" />
            <SpecResultItem label="Ft" value={result.ft} />
            <SpecResultItem label="Fd" value={result.fd} />
            <SpecResultItem label="Fh" value={result.fh} />
            <SpecResultItem label="Fv" value={result.fv} />
            <SpecResultItem label="HPet" value={result.hpet} abbr=" hp" />
          </SpecResultSection>
        </div>
      );
    }
  }

  renderIcon = () => {
    let type = 'delete';
    let method = this.onDismissHandler;

    if (this.state.isRevealed) {
      type = 'navigatedown';
      method = this.onCloseHandler;
    }

    return <Icon type={type} size={24} onClick={method} />;
  }

  renderResultsDetails = () => {
    const { result, isValid, warnings } = this.props;
    const bushingIssue = warnings['bushingCatalog'] || warnings['loadCapcity'];

    if (isValid) {
      return ([
        <SpecResultItem key={0} label="Bushing Kit" component={this.renderBushingOptions()} issue={bushingIssue && true} />,
        <SpecResultItem key={1} label="Motor Mount" value={result.motorMount} />,
        <SpecResultItem key={2} label="Backstop" value={result.backstop} />
      ]);
    }

    return (
      <p style={{ margin: 0 }}>Please update the form data.</p>
    );
  }

  renderResults = () => {
    const { result, isValid } = this.props;
    const { smr } = result;

    const gearboxComponent = <Gearbox>{isValid && smr ? result.smr : 'INVALID ENTRY'}</Gearbox>;

    const buttonComponent = !isValid || this.state.isRevealed
      ? null
      : <ActionButton icon="view" onClick={this.revealDetails}>Reveal Details</ActionButton>;

    return (
      <SpecResultSection title="Suggested Parts" before={gearboxComponent} after={buttonComponent}>
        {this.renderResultsDetails()}
      </SpecResultSection>
    );
  }

  renderWarnings() {
    const warnings = this.props.warnings;
    const keys = Object.keys(warnings);
    if (keys.length === 0) {
      return;
    }
    const title = <WarningTitle>
      <span className="ss-alert"> </span>
      <span>Warnings</span>
    </WarningTitle>;
    return (
      <Warnings>
        <SpecResultSection title={title}>
          {keys.map(key => {
            const warning = this.props.warnings[key];
            return <Warning key={key}>{renderSafeHTML(warning.message)}</Warning>;
          })}
        </SpecResultSection>
      </Warnings>
    );
  }

  revealDetails = (isRevealed: boolean = true) => {
    this.setState({ isRevealed }, () => {
      if (this.props.onReveal && this.state.isRevealed) {
        this.props.onReveal();
      }
      this.toggleOverflow(!this.state.isRevealed);
    });
  }

  render() {
    const isRevealed = this.state.isRevealed && this.props.isValid;

    return (
      <div>
        <Display isRevealed={isRevealed}>
          {this.renderIcon()}
          {this.renderResults()}
          {isRevealed && this.renderWarnings()}
          {isRevealed && this.renderCalculations()}
        </Display>
      </div>
    );
  }

}

export default SpecResultDisplay;



// WEBPACK FOOTER //
// ./src/components/SpecResultDisplay.js